import React, { useState, Children } from 'react';
import { PlusIcon, EditIcon, TrashIcon, BuildingIcon, MapPinIcon, UsersIcon, FolderIcon, CheckCircleIcon, XCircleIcon, Building, Building2, Home, Store, Warehouse, MapPin, Map, Globe, Users, User, UserCircle, Briefcase, Package, Archive, Folder, FolderOpen, FileText, Target, Flag, Zap, Settings, Hammer, Construction, BoxIcon } from 'lucide-react';
import { OrgUnitTypeForm } from './OrgUnitTypeForm';
interface OrgUnitType {
  id: string;
  code: string;
  name: string;
  description?: string;
  icon?: string;
  isActive: boolean;
  allowsChildren: boolean;
  canBeRoot: boolean;
}
// Icon mapping for dynamic icon rendering
const ICON_MAP: Record<string, any> = {
  Building,
  Building2,
  Home,
  Store,
  Warehouse,
  MapPin,
  Map,
  Globe,
  Users,
  User,
  UserCircle,
  Briefcase,
  Package,
  BoxIcon,
  Archive,
  Folder,
  FolderOpen,
  FileText,
  Target,
  Flag,
  Zap,
  Settings,
  BoxIcon,
  Hammer,
  Construction
};
export function OrgUnitTypeManager({
  locale = 'en-US'
}) {
  const [orgUnitTypes, setOrgUnitTypes] = useState<OrgUnitType[]>([{
    id: 'type-001',
    code: 'ENTITY',
    name: 'Legal Entity',
    description: 'A legal entity within the organisation',
    icon: 'Building',
    isActive: true,
    allowsChildren: true,
    canBeRoot: true
  }, {
    id: 'type-002',
    code: 'SITE',
    name: 'Physical Site',
    description: 'A physical location or site',
    icon: 'MapPin',
    isActive: true,
    allowsChildren: true,
    canBeRoot: false
  }, {
    id: 'type-003',
    code: 'DEPT',
    name: 'Department',
    description: 'An organisational department',
    icon: 'Users',
    isActive: true,
    allowsChildren: true,
    canBeRoot: false
  }, {
    id: 'type-004',
    code: 'PROJECT',
    name: 'Project',
    description: 'A project or initiative',
    icon: 'Target',
    isActive: true,
    allowsChildren: false,
    canBeRoot: false
  }]);
  const [showForm, setShowForm] = useState(false);
  const [editingType, setEditingType] = useState<OrgUnitType | null>(null);
  const handleAdd = () => {
    setEditingType(null);
    setShowForm(true);
  };
  const handleEdit = (type: OrgUnitType) => {
    setEditingType(type);
    setShowForm(true);
  };
  const handleSave = (data: any) => {
    if (editingType) {
      // Update existing type
      setOrgUnitTypes(orgUnitTypes.map(type => type.id === editingType.id ? {
        ...type,
        ...data
      } : type));
    } else {
      // Create new type
      const newType: OrgUnitType = {
        id: `type-${Date.now()}`,
        ...data
      };
      setOrgUnitTypes([...orgUnitTypes, newType]);
    }
    setShowForm(false);
    setEditingType(null);
  };
  const handleDelete = (type: OrgUnitType) => {
    if (window.confirm(`Are you sure you want to delete "${type.name}"? This action cannot be undone.`)) {
      setOrgUnitTypes(orgUnitTypes.filter(t => t.id !== type.id));
    }
  };
  const getIcon = (iconName?: string) => {
    if (iconName && ICON_MAP[iconName]) {
      return ICON_MAP[iconName];
    }
    return FolderIcon;
  };
  const getIconColor = (code: string) => {
    switch (code) {
      case 'ENTITY':
        return 'bg-indigo-100 text-indigo-600';
      case 'SITE':
        return 'bg-green-100 text-green-600';
      case 'DEPT':
        return 'bg-blue-100 text-blue-600';
      case 'PROJECT':
        return 'bg-purple-100 text-purple-600';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  };
  return <div data-id="element-20070">
      <div className="mb-6 flex justify-between items-center" data-id="element-20071">
        <div data-id="element-20072">
          <h3 className="text-lg font-medium text-gray-900" data-id="element-20073">
            Organisation Unit Types
          </h3>
          <p className="mt-1 text-sm text-gray-500" data-id="element-20074">
            Define the types of organisational units available in your system
          </p>
        </div>
        <button onClick={handleAdd} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-20075">
          <PlusIcon className="h-4 w-4 mr-1" data-id="element-20076" />
          Add Type
        </button>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3" data-id="element-20077">
        {orgUnitTypes.map(type => {
        const Icon = getIcon(type.icon);
        return <div key={type.id} className="relative bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow" data-id="element-20078">
              <div className="p-6" data-id="element-20079">
                <div className="flex items-start justify-between" data-id="element-20080">
                  <div className="flex items-center space-x-3" data-id="element-20081">
                    <div className={`flex-shrink-0 rounded-lg p-3 ${getIconColor(type.code)}`} data-id="element-20082">
                      <Icon className="h-6 w-6" data-id="element-20083" />
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-20084">
                      <h4 className="text-base font-semibold text-gray-900 truncate" data-id="element-20085">
                        {type.name}
                      </h4>
                      <p className="text-xs text-gray-500 font-mono mt-0.5" data-id="element-20086">
                        {type.code}
                      </p>
                    </div>
                  </div>
                  {!type.isActive && <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800" data-id="element-20087">
                      Inactive
                    </span>}
                </div>

                {type.description && <p className="mt-3 text-sm text-gray-600" data-id="element-20088">
                    {type.description}
                  </p>}

                <div className="mt-4 space-y-2" data-id="element-20089">
                  <div className="flex items-center text-sm" data-id="element-20090">
                    {type.allowsChildren ? <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2" data-id="element-20091" /> : <XCircleIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-20092" />}
                    <span className={type.allowsChildren ? 'text-gray-700' : 'text-gray-500'} data-id="element-20093">
                      Can have children
                    </span>
                  </div>
                  <div className="flex items-center text-sm" data-id="element-20094">
                    {type.canBeRoot ? <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2" data-id="element-20095" /> : <XCircleIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-20096" />}
                    <span className={type.canBeRoot ? 'text-gray-700' : 'text-gray-500'} data-id="element-20097">
                      Can be root level
                    </span>
                  </div>
                </div>
              </div>

              <div className="border-t border-gray-100 bg-gray-50 px-6 py-3 flex justify-end space-x-2" data-id="element-20098">
                <button onClick={() => handleEdit(type)} className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 rounded-md transition-colors" title="Edit" data-id="element-20099">
                  <EditIcon className="h-4 w-4 mr-1" data-id="element-20100" />
                  Edit
                </button>
                <button onClick={() => handleDelete(type)} className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors" title="Delete" data-id="element-20101">
                  <TrashIcon className="h-4 w-4 mr-1" data-id="element-20102" />
                  Delete
                </button>
              </div>
            </div>;
      })}
      </div>

      {/* Form Modal */}
      {showForm && <OrgUnitTypeForm orgUnitType={editingType} onClose={() => {
      setShowForm(false);
      setEditingType(null);
    }} onSave={handleSave} locale={locale} data-id="element-20103" />}
    </div>;
}